import toast from 'react-hot-toast';

import SiftConditionBuilderCompact from '@poseidon/components/SiftConditionBuilder/SiftConditionBuilderCompact';
import { svrExtractAtVariables } from '@poseidon/shared-utils/message';

import { fakeAtVariables } from '@/clientOnlyUtils/clientUtils';
import { useCreateReminderMutation } from '@/hooks/trpc/useReminderMutation';
import { api } from '@/utils/api';
import type { TemplateBlocksSectionProps } from '~/components/TemplateBlocksSection/TemplateBlocksSection';

type Contact = Parameters<typeof svrExtractAtVariables>[1];
type ContactOrg = Parameters<typeof svrExtractAtVariables>[2];

/**
 * Shared editor wiring for the touch-campaign graph / block editor — the same
 * document / reminder / sift / @-variable plumbing that feeds both the template
 * page (`/templates/[id]`) and an embedded destination editor (campaign switch).
 *
 * Returns everything `TouchCampaignGraph` / `TemplateBlocksSection` need EXCEPT the
 * template itself; spread it alongside a `cadenceTemplate`. Pass an open contact to
 * resolve @-mention chips against it (the template page does when a contact is open
 * in the side panel); omit it to use the team's default variable values.
 */
export function useTouchEditorContext(
  userTeam: TemplateBlocksSectionProps['userTeam'],
  opts?: { contact?: Contact; contactOrg?: ContactOrg },
): Omit<TemplateBlocksSectionProps, 'cadenceTemplate'> {
  const documentsQuery = api.sharing.all.useQuery({ teamId: userTeam.teamId });
  const reminderActionsQuery = api.reminderAction.all.useQuery({ teamId: userTeam.teamId });
  const createReminderMutation = useCreateReminderMutation();

  const atVariables = {
    ...fakeAtVariables,
    ...svrExtractAtVariables(userTeam, opts?.contact ?? null, opts?.contactOrg ?? null),
  };

  return {
    userTeam,
    atVariables,
    documents: documentsQuery.data?.docs,
    // Mirrors the contact-detail-page wiring so the inline editor's reminder
    // dropdown + sift condition builder actually render with data.
    reminderActions: reminderActionsQuery.data?.reminderActions ?? [],
    reminderActionsLoading: reminderActionsQuery.isLoading,
    onCreateReminder: async (name) => {
      await createReminderMutation.mutateAsync({ teamId: userTeam.teamId, data: { name } });
    },
    reminderCreating: createReminderMutation.isPending,
    onReminderError: (message) => toast.error(message),
    renderSiftConditionBuilder: (props) => (
      <SiftConditionBuilderCompact
        value={props.value as Parameters<typeof SiftConditionBuilderCompact>[0]['value']}
        onChange={(v) => props.onChange(v as typeof props.value)}
      />
    ),
  };
}
